import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
    exerciseSchedules: [],
    currentExerciseSchedule: null,
    isLoading: false,
    error: null,
};

// ==================== Async Thunks ====================

// Fetch all Exercise Schedules
export const fetchExerciseSchedules = createAsyncThunk(
    'exerciseSchedules/fetchExerciseSchedules',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/exercise-schedules');
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch Exercise Schedules'
            );
        }
    }
);

// Fetch a single Exercise Schedule
export const fetchExerciseSchedule = createAsyncThunk(
    'exerciseSchedules/fetchExerciseSchedule',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/exercise-schedules/${id}`);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch Exercise Schedule'
            );
        }
    }
);

// Create a new Exercise Schedule
export const createExerciseSchedule = createAsyncThunk(
    'exerciseSchedules/createExerciseSchedule',
    async (scheduleData, { rejectWithValue }) => {
        try {
            const response = await api.post('/exercise-schedules', scheduleData);
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to create Exercise Schedule'
            );
        }
    }
);

// Update an existing Exercise Schedule
export const updateExerciseSchedule = createAsyncThunk(
    'exerciseSchedules/updateExerciseSchedule',
    async ({ case_id, scheduleData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/exercise-schedules/case/${case_id}`, scheduleData);
            return response.data.data ?? response.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to update Exercise Schedule'
            );
        }
    }
);

// Delete an Exercise Schedule
export const deleteExerciseSchedule = createAsyncThunk(
    'exerciseSchedules/deleteExerciseSchedule',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/exercise-schedules/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to delete Exercise Schedule'
            );
        }
    }
);

// ==================== Slice ====================
const exerciseSchedulesSlice = createSlice({
    name: 'exerciseSchedules',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentExerciseSchedule: (state, action) => {
            state.currentExerciseSchedule = action.payload;
        },
        clearCurrentExerciseSchedule: (state) => {
            state.currentExerciseSchedule = null;
        },
        resetExerciseSchedulesState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            // FETCH ALL
            .addCase(fetchExerciseSchedules.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchExerciseSchedules.fulfilled, (state, action) => {
                state.isLoading = false;
                state.exerciseSchedules = action.payload;
            })
            .addCase(fetchExerciseSchedules.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // FETCH SINGLE
            .addCase(fetchExerciseSchedule.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchExerciseSchedule.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentExerciseSchedule = action.payload;
            })
            .addCase(fetchExerciseSchedule.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // CREATE
            .addCase(createExerciseSchedule.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createExerciseSchedule.fulfilled, (state, action) => {
                state.isLoading = false;
                state.exerciseSchedules.unshift(action.payload);
            })
            .addCase(createExerciseSchedule.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // UPDATE
            .addCase(updateExerciseSchedule.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateExerciseSchedule.fulfilled, (state, action) => {
                state.isLoading = false;
                const updated = action.payload.data ?? action.payload;

                const index = state.exerciseSchedules.findIndex((d) => d.id === updated.id);
                if (index !== -1) {
                    state.exerciseSchedules[index] = updated;
                }

                if (state.currentExerciseSchedule?.id === updated.id) {
                    state.currentExerciseSchedule = updated;
                }
            })
            .addCase(updateExerciseSchedule.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // DELETE
            .addCase(deleteExerciseSchedule.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteExerciseSchedule.fulfilled, (state, action) => {
                state.isLoading = false;
                state.exerciseSchedules = state.exerciseSchedules.filter(
                    (d) => d.id !== action.payload
                );

                if (state.currentExerciseSchedule?.id === action.payload) {
                    state.currentExerciseSchedule = null;
                }
            })
            .addCase(deleteExerciseSchedule.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearError,
    setCurrentExerciseSchedule,
    clearCurrentExerciseSchedule,
    resetExerciseSchedulesState,
} = exerciseSchedulesSlice.actions;

export default exerciseSchedulesSlice.reducer;